'use es6';

import PropTypes from 'prop-types';
import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import I18n from 'I18n';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { H6 } from 'foundations-components/transitional/components/H6';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Tag } from 'foundations-components/transitional/components/Tag';
import EPPreviewIframe from 'editor-components/preview/EPPreviewIframe';
import EPFullScreenModal from 'editor-components/preview/EPFullScreenModal';
import withUsageTracking from 'editor-components/hoc/withUsageTracking';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Small } from 'foundations-components/transitional/components/Small';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Box } from 'foundations-components/transitional/components/Box';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CURRENT_REVISION_ID = -1;
const BUFFER_REVISION_ID = 'buffer-revision';
const SIDEBAR_WIDTH = '380px';
const RevisionsSidebar = styled.div.withConfig({
  displayName: "EPRevisionsModal__RevisionsSidebar",
  componentId: "sc-18rbjkq-0"
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: `${SIDEBAR_WIDTH}`,
  borderRight: `1px solid ${theme.color['border-core-default']}`
}));
const RevisionsList = styled.div.withConfig({
  displayName: "EPRevisionsModal__RevisionsList",
  componentId: "sc-18rbjkq-1"
})(({
  theme
}) => ({
  height: 'calc(100% - 84px)' /* The px height of the sidebar header */,
  overflow: 'auto',
  "button[aria-pressed='true']": {
    background: theme.color['fill-field-hover']
  },
  'button:hover': {
    background: theme.color['fill-field-hover']
  }
}));
class EPRevisionsModal extends Component {
  constructor(props) {
    super(props);
    this.isBufferSelected = () => {
      const {
        selectedRevision
      } = this.state;
      return selectedRevision.id === BUFFER_REVISION_ID;
    };
    this.updateSelectedRevision = revision => {
      const {
        selectedRevision
      } = this.state;
      if (revision.id !== selectedRevision.id) {
        this.setState({
          selectedRevision: revision
        });
      }
    };
    this.loadMoreRevisions = () => {
      const {
        hasMoreRevisions,
        onLoadMoreRevisions
      } = this.props;
      this.setState({
        isLoadingMoreRevisions: true
      });
      if (hasMoreRevisions) {
        onLoadMoreRevisions();
      }
    };
    this.buildPreviewURL = () => {
      const {
        getPreviewUrl
      } = this.props;
      const {
        selectedRevision
      } = this.state;
      return getPreviewUrl(selectedRevision, selectedRevision.id === BUFFER_REVISION_ID);
    };
    this.chooseRevision = () => {
      const {
        selectedRevision
      } = this.state;
      const {
        onChooseRevision,
        onTrackUsage
      } = this.props;
      this.setState({
        revertingContent: true,
        hideHeaderErrorMessage: false
      });
      onTrackUsage('choose revision click');
      onChooseRevision(selectedRevision);
    };
    this.handleHeaderErrorClose = () => {
      this.setState({
        hideHeaderErrorMessage: true
      });
    };
    this.isLatestPublishedVersion = revision => {
      const {
        revisions
      } = this.props;
      if (revision.isScheduledUpdate || revision.versionType !== 'STANDARD') {
        return false;
      }
      const publishedRevisions = revisions.filter(r => !r.isScheduledUpdate && r.object && r.object.isPublished).sort((r1, r2) => r2.updatedAt - r1.updateAt);
      return publishedRevisions.length > 0 && publishedRevisions[0].id === revision.id;
    };
    this.isScheduledVersion = revision => {
      return revision.object && revision.object.currentState === 'SCHEDULED' || revision.isScheduledUpdate;
    };
    this.isSavedVersion = revision => {
      return revision.versionType === 'DRAFT' && !this.isCurrentDraft(revision) && !revision.warningMessageString;
    };
    this.isPublishedVersion = revision => {
      if (revision.isScheduledUpdate) {
        return false;
      }

      // Add a check for Published Structured content type
      // To be removed after moving to case-study-ui, consumers to transform their revisions into VersionObject type
      const revisionObject = revision.object;
      if (revisionObject && revisionObject.type === 'CASE_STUDY') {
        var _revisionObject$value;
        if (((_revisionObject$value = revisionObject.values) === null || _revisionObject$value === void 0 ? void 0 : _revisionObject$value.hs_state) !== 'PUBLISHED') {
          return false;
        }
      } else if (revisionObject && !revisionObject.isPublished) {
        return false;
      }
      return revision.versionType === 'STANDARD';
    };
    this.isCurrentDraft = revision => {
      const {
        hasBufferedChanges
      } = this.props;
      const {
        mostRecentRevisionId
      } = this.state;
      if (this.isLatestPublishedVersion(revision)) {
        return false;
      }
      if (hasBufferedChanges) {
        return revision.id === BUFFER_REVISION_ID;
      }
      return revision.id === mostRecentRevisionId;
    };
    this.getMostRecentRevisionId = () => {
      var _revisions$reduce;
      const {
        revisions
      } = this.props;
      return (_revisions$reduce = revisions.reduce((acc, current) => {
        if (current.updatedAt && (!acc || current.updatedAt > acc.updatedAt)) {
          return current;
        }
        return acc;
      }, null)) === null || _revisions$reduce === void 0 ? void 0 : _revisions$reduce.id;
    };
    this.renderRevision = (revision, index) => {
      const {
        selectedRevision
      } = this.state;
      const {
        shouldShowCurrentDraftLabel
      } = this.props;
      const revisionTimestamp = revision.updatedAt;
      const timestamp = revisionTimestamp && !isNaN(revisionTimestamp) ? I18n.moment.portalTz(revisionTimestamp).format('l LT') : '';
      const isActive = revision.id === selectedRevision.id;
      return /*#__PURE__*/_jsx(Button, {
        active: isActive,
        "aria-pressed": isActive,
        block: true,
        "data-test-id": "revision-list-item",
        onClick: () => this.updateSelectedRevision(revision),
        paddingInline: "700",
        paddingBlock: "400",
        role: "radio",
        use: "unstyled",
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          alignItems: "flex-start",
          inlineSize: "100%",
          children: [/*#__PURE__*/_jsx(H6, {
            marginBlockEnd: "200",
            children: timestamp
          }), /*#__PURE__*/_jsx("div", {
            "data-test-id": "revision-list-item-author",
            children: this.renderRevisionAuthor(revision)
          }), (this.isLatestPublishedVersion(revision) || this.isPublishedVersion(revision)) && /*#__PURE__*/_jsx(Tag, {
            use: "info",
            marginBlockStart: "100",
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'epRevisionsModal.publishedVersion'
            })
          }), this.isScheduledVersion(revision) && /*#__PURE__*/_jsx(Tag, {
            use: "scheduled",
            marginBlockStart: "100",
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'epRevisionsModal.scheduledVersionLabel'
            })
          }), this.isSavedVersion(revision) && /*#__PURE__*/_jsx(Tag, {
            use: "info",
            marginBlockStart: "100",
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'epRevisionsModal.savedVersion'
            })
          }), this.isCurrentDraft(revision) && shouldShowCurrentDraftLabel && /*#__PURE__*/_jsx(Tag, {
            use: "thunderdome",
            marginBlockStart: "100",
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "epRevisionsModal.currentVersion"
            })
          }), revision.warningMessageString && /*#__PURE__*/_jsx(Small, {
            "data-test": "version-warning",
            children: revision.warningMessageString
          })]
        })
      }, index);
    };
    const selectedRevisionToStart = !props.isPublished && props.hasBufferedChanges ? BUFFER_REVISION_ID : this.getMostRecentRevisionId() || CURRENT_REVISION_ID;
    this.state = {
      mostRecentRevisionId: this.getMostRecentRevisionId(),
      selectedRevision: {
        id: selectedRevisionToStart
      },
      revertingContent: false,
      hideHeaderErrorMessage: true,
      isLoadingMoreRevisions: false
    };
  }
  componentDidUpdate(prevProps) {
    const {
      headerErrorMessage,
      isLoadingInitialRevisions,
      revisions,
      isPublished,
      hasBufferedChanges
    } = this.props;
    if (!prevProps.headerErrorMessage && headerErrorMessage) {
      this.setState({
        revertingContent: false
      });
    }
    if (prevProps.revisions && prevProps.revisions.length !== this.props.revisions.length) {
      this.setState({
        isLoadingMoreRevisions: false
      });
    }
    if (prevProps.revisions !== revisions) {
      this.setState({
        mostRecentRevisionId: this.getMostRecentRevisionId()
      });
    }
    if (prevProps.isLoadingInitialRevisions !== isLoadingInitialRevisions) {
      const scheduledRevision = revisions.find(rev => rev.isScheduledUpdate);
      if (scheduledRevision) {
        this.setState({
          selectedRevision: scheduledRevision
        });
      } else if (prevProps.revisions.length === 0) {
        // Update component state selected revision with initial most recent revision
        const initialRevisionId = !isPublished && hasBufferedChanges ? BUFFER_REVISION_ID : this.getMostRecentRevisionId();
        const initialRevision = revisions.find(rev => rev.id === initialRevisionId);
        if (initialRevision) {
          this.setState({
            selectedRevision: initialRevision
          });
        }
      }
    }
  }
  renderHeaderError() {
    const {
      hideHeaderErrorMessage
    } = this.state;
    const {
      headerErrorMessage
    } = this.props;
    if (!hideHeaderErrorMessage && headerErrorMessage) {
      return /*#__PURE__*/_jsx("div", {
        style: {
          maxHeight: '38px'
        },
        children: /*#__PURE__*/_jsx(Alert, {
          "data-test-id": "revisions-modal-header-error",
          closeable: true,
          onClose: this.handleHeaderErrorClose,
          maxInlineSize: "100%",
          type: "danger",
          use: "inline",
          children: headerErrorMessage
        })
      });
    }
    return null;
  }
  renderRevisionPreview() {
    const {
      selectedRevision
    } = this.state;
    const {
      getFormDataForPreview,
      getFormUrlForPreview,
      renderPreview,
      Preview
    } = this.props;
    let preview;
    if (Preview) {
      preview = /*#__PURE__*/_jsx(Preview, {
        selectedRevision: selectedRevision
      });
    } else if (typeof renderPreview === 'function') {
      preview = renderPreview({
        selectedRevision
      });
    } else {
      preview = /*#__PURE__*/_jsx(EPPreviewIframe, {
        previewUrl: this.buildPreviewURL(),
        formUrl: getFormUrlForPreview(selectedRevision),
        formData: getFormDataForPreview(selectedRevision)
      });
    }
    return /*#__PURE__*/_jsx(Box, {
      flexGrow: 1,
      children: preview
    });
  }
  renderRevisionAuthor(revision) {
    var _ref, _revision$user$fullNa, _revision$user, _revision$user2, _revision$object;
    const nameOrEmail = (_ref = (_revision$user$fullNa = (_revision$user = revision.user) === null || _revision$user === void 0 ? void 0 : _revision$user.fullName) !== null && _revision$user$fullNa !== void 0 ? _revision$user$fullNa : (_revision$user2 = revision.user) === null || _revision$user2 === void 0 ? void 0 : _revision$user2.email) !== null && _ref !== void 0 ? _ref : (_revision$object = revision.object) === null || _revision$object === void 0 ? void 0 : _revision$object.authorName;
    return nameOrEmail ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: 'epRevisionsModal.changesBy',
      options: {
        nameOrEmail
      }
    }) : null;
  }
  renderBufferRevision() {
    const {
      lastBufferAuthor,
      lastSave
    } = this.props;
    const revision = {
      id: BUFFER_REVISION_ID,
      object: {
        authorName: lastBufferAuthor
      },
      updatedAt: lastSave
    };
    return this.renderRevision(revision, BUFFER_REVISION_ID);
  }
  renderRevisionsSidebar() {
    const {
      hasMoreRevisions,
      revisions,
      paginationLimit,
      isLoadingInitialRevisions,
      hasBufferedChanges,
      subtitle,
      sidebarNavMarker
    } = this.props;
    const {
      isLoadingMoreRevisions
    } = this.state;
    const content = isLoadingInitialRevisions ? /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [sidebarNavMarker, /*#__PURE__*/_jsxs(RevisionsList, {
        children: [!revisions.length && /*#__PURE__*/_jsx(Alert, {
          type: "warning",
          marginInline: "700",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "epRevisionsModal.noRevisionsText"
          })
        }), /*#__PURE__*/_jsxs(ButtonGroup, {
          display: "flex",
          role: "radiogroup",
          orientation: "vertical",
          children: [hasBufferedChanges && this.renderBufferRevision(), revisions.map(this.renderRevision)]
        }), isLoadingMoreRevisions && /*#__PURE__*/_jsx(LoadingSpinner, {
          size: 'extra-small',
          marginInlineStart: "800"
        }), hasMoreRevisions && !isLoadingMoreRevisions && /*#__PURE__*/_jsx(Button, {
          "data-test-id": "load-more-revisions",
          onClick: this.loadMoreRevisions,
          paddingInlineStart: "700",
          paddingBlockEnd: subtitle ? '300' : '0',
          size: "md",
          use: "link",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "epRevisionsModal.loadMoreButtonText",
            options: {
              count: paginationLimit
            }
          })
        })]
      })]
    });
    return /*#__PURE__*/_jsxs(RevisionsSidebar, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h4",
        marginBlockEnd: subtitle ? '200' : '400',
        paddingBlockStart: "400",
        paddingInlineStart: "700",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'epRevisionsModal.headerText'
        })
      }), subtitle && /*#__PURE__*/_jsx(Text, {
        "data-test-id": "revisions-modal-subtitle",
        paddingInline: "700",
        as: "p",
        children: /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: subtitle
        })
      }), content]
    });
  }
  renderHeaderButton() {
    const {
      revertingContent
    } = this.state;
    const {
      disabled,
      revisions,
      shouldRestoreToBuffer,
      preventRevertLoading
    } = this.props;
    const _disabled = this.isBufferSelected() || disabled;
    const button = /*#__PURE__*/_jsx(Tooltip, {
      disabled: !this.isBufferSelected(),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'components.modals.revisionsViewModal.cantRestoreBufferTooltip'
      }),
      placement: "top right",
      children: /*#__PURE__*/_jsx(LoadingButton, {
        loading: !preventRevertLoading && revertingContent,
        use: "primary",
        onClick: this.chooseRevision,
        disabled: _disabled || !revisions.length,
        "data-test-id": "select-revision-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `epRevisionsModal.restoreRevision`
        })
      })
    });
    if (revisions.length && !revertingContent && shouldRestoreToBuffer) {
      return /*#__PURE__*/_jsx(Tooltip, {
        placement: "bottom left",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'epRevisionsModal.publishWarning'
        }),
        children: button
      });
    }
    return button;
  }
  render() {
    const {
      onClose
    } = this.props;
    return /*#__PURE__*/_jsx(EPFullScreenModal, {
      onClose: onClose,
      headerContent: this.renderHeaderError(),
      headerButton: this.renderHeaderButton(),
      children: /*#__PURE__*/_jsxs(Flex, {
        blockSize: "100%",
        children: [this.renderRevisionsSidebar(), this.renderRevisionPreview()]
      })
    });
  }
}
EPRevisionsModal.propTypes = {
  disabled: PropTypes.bool.isRequired,
  getPreviewUrl: PropTypes.func,
  getFormDataForPreview: PropTypes.func.isRequired,
  getFormUrlForPreview: PropTypes.func.isRequired,
  hasMoreRevisions: PropTypes.bool.isRequired,
  headerErrorMessage: PropTypes.string,
  onChooseRevision: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onLoadMoreRevisions: PropTypes.func.isRequired,
  onTrackUsage: PropTypes.func.isRequired,
  paginationLimit: PropTypes.number.isRequired,
  revisions: PropTypes.array.isRequired,
  shouldRestoreToBuffer: PropTypes.bool.isRequired,
  totalRevisionCount: PropTypes.number.isRequired,
  isLoadingInitialRevisions: PropTypes.bool.isRequired,
  preventRevertLoading: PropTypes.bool,
  renderPreview: PropTypes.func,
  subtitle: PropTypes.node,
  isPublished: PropTypes.bool,
  hasBufferedChanges: PropTypes.bool,
  lastBufferAuthor: PropTypes.string,
  lastSave: PropTypes.instanceOf(Date),
  shouldShowCurrentDraftLabel: PropTypes.bool,
  sidebarNavMarker: PropTypes.node
};
EPRevisionsModal.defaultProps = {
  disabled: false,
  getFormDataForPreview: emptyFunction,
  getFormUrlForPreview: emptyFunction,
  getPreviewUrl: emptyFunction,
  revisions: [],
  shouldRestoreToBuffer: true,
  preventRevertLoading: false,
  isPublished: true,
  hasBufferedChanges: false,
  shouldShowCurrentDraftLabel: true,
  sidebarNavMarker: null
};
export default withUsageTracking(EPRevisionsModal);